import { useMutation } from '@apollo/client';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import React from 'react';
import { Label } from '../../components/Form/Label/Label';
import { Spacer } from '../../components/Spacer/Spacer';
import { Stack } from '../../components/Stack/Stack';
import { graphql } from '../../generated';
import { t } from '../../locales/translate';
import { handleError } from '../../model/notification';
import { FormForm } from '../../components/Form/FormForm';
import { useModal } from '../../components/Modal/PresentedModal';
import { FormSearch } from '../../components/Form/AsyncSearch/FormSearch';
import { FormRadioGroup } from '../../components/Form/Radio/FormRadioGroup';

interface Props {
  shopId: string;
}

const CREATE_SHOP_OWNERSHIP = graphql(`
  mutation CreateShopOwnership($shopId: ID!, $companyId: ID!, $role: ShopOwnershipRole!) {
    createShopOwnership(input: { shopId: $shopId, companyId: $companyId, role: $role }) {
      shopOwnership {
        id
      }
    }
  }
`);

export function CreateShopOwnership({ shopId }: Props): JSX.Element {
  const { open, Modal, close } = useModal();
  const [create] = useMutation(CREATE_SHOP_OWNERSHIP);

  const { fields, valid, dirty, onSubmit } = useForm<{
    shopId: string;
    companyId?: string;
    role?: 'EDITOR' | 'OBSERVER';
  }>({
    model: {
      shopId,
    },
    validations: {
      companyId: 'required',
      role: 'required',
    },
    onSubmit: async ({ model }) => {
      if (model.companyId && model.role) {
        await create({
          variables: {
            shopId: shopId,
            companyId: model.companyId,
            role: model.role,
          },
        });
        close();
      }
    },
    onSubmitError: handleError,
  });

  return (
    <Modal open={open} title={t.actions.assign(t.model.company._name)}>
      <FormForm onSubmit={onSubmit} onClose={close} canSubmit={dirty && valid}>
        <Stack>
          <FormSearch
            {...fields.companyId}
            name="companyId"
            searchIn={['company']}
            label={t.actions.select(t.model.company._name)}
          />
          <Spacer size="none" />
          <Label title={t.model.permissions.setPermissionsForThisShop} />
          <FormRadioGroup
            {...fields.role}
            name="role"
            options={[
              {
                label: t.model.role.observer,
                value: 'OBSERVER',
              },
              {
                label: t.model.role.editor,
                value: 'EDITOR',
              },
            ]}
          />
        </Stack>
      </FormForm>
    </Modal>
  );
}
